import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { LayerGroup, Ramp } from "../../../../../types";
import { Shape } from "../RampCanvasV2/RampCanvasV2.types";
import {
  canMoveGroupShapesDown,
  canMoveGroupShapesUp,
  canMoveShapeDown,
  canMoveShapeDownVisual,
  canMoveShapeUp,
  canMoveShapeUpVisual,
  moveGroupShapesDown,
  moveGroupShapesUp,
  moveShapeDownGroupAware,
  moveShapeDownVisual,
  moveShapeUpGroupAware,
  moveShapeUpVisual,
} from "./utils";

/**
 * Apply sort_order to shapes based on array position.
 * This ensures the visual ordering is persisted to the database.
 */
const applyShapeSortOrder = (shapes: Shape[]): Shape[] => {
  return shapes.map((s, index) => ({ ...s, sort_order: index }));
};

/**
 * All shape and group mutation operations.
 * Every operation is atomic (single setRamp call).
 * All operations use IDs, never indices.
 */
export interface LayerOperations {
  // Shape operations
  moveShape: (shapeId: string, direction: "up" | "down") => void;
  renameShape: (shapeId: string, newName: string) => void;
  deleteShape: (shapeId: string) => void;
  deleteShapes: (shapeIds: string[]) => void;
  toggleVisibility: (shapeId: string) => void;
  toggleLock: (shapeId: string) => void;
  toggleLockMultiple: (shapeIds: string[]) => void;

  // Group operations
  createGroup: (shapeIds: string[], groupName?: string) => void;
  deleteGroup: (groupId: string) => void;
  renameGroup: (groupId: string, newName: string) => void;
  moveGroup: (groupId: string, direction: "up" | "down") => void;
  toggleGroupLock: (groupId: string) => void;

  // Group membership
  addToGroup: (shapeId: string, groupId: string) => void;
  removeFromGroup: (shapeId: string) => void;
  ungroupShapes: (shapeIds: string[]) => void;

  // Predicates
  canMoveShapeUp: (shapeId: string) => boolean;
  canMoveShapeDown: (shapeId: string) => boolean;
  canMoveGroupUp: (groupId: string) => boolean;
  canMoveGroupDown: (groupId: string) => boolean;
  isGroupLocked: (groupId: string) => boolean;
}

type RampUpdater = (rampOrUpdater: Ramp | ((prevRamp: Ramp) => Ramp)) => void;

/**
 * Hook for all layer panel state mutations.
 *
 * Key principles:
 * 1. Every operation produces exactly ONE setRamp call (atomic)
 * 2. All operations use IDs, never array indices
 * 3. No stale closure issues - always reads from latest state
 */
export function useLayerOperations(
  ramp: Ramp,
  setRamp: RampUpdater
): LayerOperations {
  const shapes = ramp.shapes || [];
  const groups = ramp.layer_groups || [];

  // ============= Shape Operations =============

  /**
   * Move a shape up or down in the visual order.
   * - Ungrouped shapes move among other ungrouped shapes
   * - Grouped shapes move within their group
   */
  const moveShape = React.useCallback(
    (shapeId: string, direction: "up" | "down") => {
      setRamp((prev) => {
        const currentShapes = prev.shapes || [];
        const newShapes =
          direction === "up"
            ? moveShapeUpVisual(currentShapes, shapeId)
            : moveShapeDownVisual(currentShapes, shapeId);
        return { ...prev, shapes: applyShapeSortOrder(newShapes) };
      });
    },
    [setRamp]
  );

  const renameShape = React.useCallback(
    (shapeId: string, newName: string) => {
      setRamp((prev) => ({
        ...prev,
        shapes: (prev.shapes || []).map((s) =>
          s.id === shapeId ? { ...s, display_name: newName } : s
        ),
      }));
    },
    [setRamp]
  );

  const deleteShape = React.useCallback(
    (shapeId: string) => {
      setRamp((prev) => ({
        ...prev,
        shapes: (prev.shapes || []).filter((s) => s.id !== shapeId),
      }));
    },
    [setRamp]
  );

  const deleteShapes = React.useCallback(
    (shapeIds: string[]) => {
      const idsToDelete = new Set(shapeIds);
      setRamp((prev) => ({
        ...prev,
        shapes: (prev.shapes || []).filter((s) => !idsToDelete.has(s.id)),
      }));
    },
    [setRamp]
  );

  const toggleVisibility = React.useCallback(
    (shapeId: string) => {
      setRamp((prev) => ({
        ...prev,
        shapes: (prev.shapes || []).map((s) =>
          s.id === shapeId ? { ...s, visible: s.visible === false } : s
        ),
      }));
    },
    [setRamp]
  );

  const toggleLock = React.useCallback(
    (shapeId: string) => {
      setRamp((prev) => ({
        ...prev,
        shapes: (prev.shapes || []).map((s) =>
          s.id === shapeId ? { ...s, locked: !s.locked } : s
        ),
      }));
    },
    [setRamp]
  );

  const toggleLockMultiple = React.useCallback(
    (shapeIds: string[]) => {
      setRamp((prev) => {
        const currentShapes = prev.shapes || [];
        const targetShapes = currentShapes.filter((s) =>
          shapeIds.includes(s.id)
        );
        // If any are unlocked, lock all. Otherwise, unlock all.
        const anyUnlocked = targetShapes.some((s) => !s.locked);
        const idsToToggle = new Set(shapeIds);

        return {
          ...prev,
          shapes: currentShapes.map((s) =>
            idsToToggle.has(s.id) ? { ...s, locked: anyUnlocked } : s
          ),
        };
      });
    },
    [setRamp]
  );

  // ============= Group Operations =============

  const createGroup = React.useCallback(
    (shapeIds: string[], groupName?: string) => {
      if (shapeIds.length < 2) return;

      setRamp((prev) => {
        const currentShapes = prev.shapes || [];
        const currentGroups = prev.layer_groups || [];
        const selectedIds = new Set(shapeIds);

        // Check if any selected shape is already in a group
        const selectedShapes = currentShapes.filter((s) =>
          selectedIds.has(s.id)
        );
        const existingGroupId = selectedShapes.find(
          (s) => s.group_id
        )?.group_id;

        let targetGroupId: string;
        let newGroups: LayerGroup[];

        if (existingGroupId) {
          // Use existing group
          targetGroupId = existingGroupId;
          newGroups = currentGroups;
        } else {
          // Create new group
          const name = groupName || `Group ${currentGroups.length + 1}`;
          const newGroup: LayerGroup = { id: uuidv4(), name };
          targetGroupId = newGroup.id;
          newGroups = [...currentGroups, newGroup];
        }

        // Move selected shapes to be contiguous, assign to group
        const nonSelected = currentShapes.filter((s) => !selectedIds.has(s.id));
        const selected = currentShapes
          .filter((s) => selectedIds.has(s.id))
          .map((s) => ({ ...s, group_id: targetGroupId }));

        // Find insert position based on where selected shapes were originally
        let insertIdx: number;

        if (existingGroupId) {
          // Adding to existing group: insert after last existing group member
          insertIdx = nonSelected.length;
          for (let i = 0; i < nonSelected.length; i++) {
            if (nonSelected[i].group_id === targetGroupId) {
              insertIdx = i + 1;
            }
          }
        } else {
          // New group: insert at the position of the first selected shape
          // Find the minimum original index of selected shapes
          let minOriginalIndex = currentShapes.length;
          for (let i = 0; i < currentShapes.length; i++) {
            if (selectedIds.has(currentShapes[i].id)) {
              minOriginalIndex = i;
              break;
            }
          }

          // Convert to index in nonSelected array
          // Count how many non-selected shapes come before minOriginalIndex
          insertIdx = 0;
          for (let i = 0; i < minOriginalIndex; i++) {
            if (!selectedIds.has(currentShapes[i].id)) {
              insertIdx++;
            }
          }
        }

        const newShapes = [
          ...nonSelected.slice(0, insertIdx),
          ...selected,
          ...nonSelected.slice(insertIdx),
        ];

        return {
          ...prev,
          shapes: applyShapeSortOrder(newShapes),
          layer_groups: newGroups,
        };
      });
    },
    [setRamp]
  );

  const deleteGroup = React.useCallback(
    (groupId: string) => {
      setRamp((prev) => ({
        ...prev,
        // Remove group_id from all shapes in this group
        shapes: (prev.shapes || []).map((s) =>
          s.group_id === groupId ? { ...s, group_id: null } : s
        ),
        // Remove the group itself
        layer_groups: (prev.layer_groups || []).filter((g) => g.id !== groupId),
      }));
    },
    [setRamp]
  );

  const renameGroup = React.useCallback(
    (groupId: string, newName: string) => {
      setRamp((prev) => ({
        ...prev,
        layer_groups: (prev.layer_groups || []).map((g) =>
          g.id === groupId ? { ...g, name: newName } : g
        ),
      }));
    },
    [setRamp]
  );

  /**
   * Move entire group up or down in the array.
   * All shapes in the group move together as a unit.
   * Skips past entire groups when moving.
   */
  const moveGroupOp = React.useCallback(
    (groupId: string, direction: "up" | "down") => {
      setRamp((prev) => {
        const currentShapes = prev.shapes || [];
        const newShapes =
          direction === "up"
            ? moveGroupShapesUp(currentShapes, groupId)
            : moveGroupShapesDown(currentShapes, groupId);
        return { ...prev, shapes: applyShapeSortOrder(newShapes) };
      });
    },
    [setRamp]
  );

  const toggleGroupLock = React.useCallback(
    (groupId: string) => {
      setRamp((prev) => {
        const currentShapes = prev.shapes || [];
        const groupShapes = currentShapes.filter((s) => s.group_id === groupId);
        // If all are locked, unlock all. Otherwise, lock all.
        const allLocked =
          groupShapes.length > 0 && groupShapes.every((s) => s.locked);

        return {
          ...prev,
          shapes: currentShapes.map((s) =>
            s.group_id === groupId ? { ...s, locked: !allLocked } : s
          ),
        };
      });
    },
    [setRamp]
  );

  // ============= Group Membership =============

  const addToGroup = React.useCallback(
    (shapeId: string, groupId: string) => {
      setRamp((prev) => {
        const currentShapes = prev.shapes || [];
        const shapeToMove = currentShapes.find((s) => s.id === shapeId);
        if (!shapeToMove) return prev;

        // Remove shape from current position
        const otherShapes = currentShapes.filter((s) => s.id !== shapeId);

        // Find the ID of the last shape in the target group
        let lastGroupMemberId: string | null = null;
        for (const s of currentShapes) {
          if (s.group_id === groupId) {
            lastGroupMemberId = s.id;
          }
        }

        // Find insert position by ID (not index!)
        let insertIdx: number;
        if (lastGroupMemberId) {
          // Insert after the last group member
          insertIdx = otherShapes.findIndex((s) => s.id === lastGroupMemberId);
          if (insertIdx !== -1) {
            insertIdx += 1; // Insert AFTER
          } else {
            insertIdx = otherShapes.length;
          }
        } else {
          // No existing members, add at end
          insertIdx = otherShapes.length;
        }

        // Create updated shape with new group_id
        const updatedShape = { ...shapeToMove, group_id: groupId };

        // Insert at correct position
        const newShapes = [
          ...otherShapes.slice(0, insertIdx),
          updatedShape,
          ...otherShapes.slice(insertIdx),
        ];

        return { ...prev, shapes: applyShapeSortOrder(newShapes) };
      });
    },
    [setRamp]
  );

  const removeFromGroup = React.useCallback(
    (shapeId: string) => {
      setRamp((prev) => ({
        ...prev,
        shapes: (prev.shapes || []).map((s) =>
          s.id === shapeId ? { ...s, group_id: null } : s
        ),
      }));
    },
    [setRamp]
  );

  const ungroupShapes = React.useCallback(
    (shapeIds: string[]) => {
      const idsToUngroup = new Set(shapeIds);
      setRamp((prev) => ({
        ...prev,
        shapes: (prev.shapes || []).map((s) =>
          idsToUngroup.has(s.id) ? { ...s, group_id: null } : s
        ),
      }));
    },
    [setRamp]
  );

  // ============= Predicates =============

  /** Check if shape can move up in visual order */
  const canMoveShapeUpFn = React.useCallback(
    (shapeId: string) => canMoveShapeUpVisual(shapes, shapeId),
    [shapes]
  );

  /** Check if shape can move down in visual order */
  const canMoveShapeDownFn = React.useCallback(
    (shapeId: string) => canMoveShapeDownVisual(shapes, shapeId),
    [shapes]
  );

  /** Check if group can move up in array order */
  const canMoveGroupUpFn = React.useCallback(
    (groupId: string) => canMoveGroupShapesUp(shapes, groupId),
    [shapes]
  );

  /** Check if group can move down in array order */
  const canMoveGroupDownFn = React.useCallback(
    (groupId: string) => canMoveGroupShapesDown(shapes, groupId),
    [shapes]
  );

  const isGroupLockedFn = React.useCallback(
    (groupId: string) => {
      const groupShapes = shapes.filter((s) => s.group_id === groupId);
      return groupShapes.length > 0 && groupShapes.every((s) => s.locked);
    },
    [shapes]
  );

  return {
    // Shape operations
    moveShape,
    renameShape,
    deleteShape,
    deleteShapes,
    toggleVisibility,
    toggleLock,
    toggleLockMultiple,

    // Group operations
    createGroup,
    deleteGroup,
    renameGroup,
    moveGroup: moveGroupOp,
    toggleGroupLock,

    // Group membership
    addToGroup,
    removeFromGroup,
    ungroupShapes,

    // Predicates
    canMoveShapeUp: canMoveShapeUpFn,
    canMoveShapeDown: canMoveShapeDownFn,
    canMoveGroupUp: canMoveGroupUpFn,
    canMoveGroupDown: canMoveGroupDownFn,
    isGroupLocked: isGroupLockedFn,
  };
}
